import { useFormik } from "formik";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDrawMapStore, useMapMarkerStore, usePipedriveStore } from "../..";
import useClosable from "../../hooks/useClosable";
import useDealCreate from "../../hooks/useDealCreate";
import useNavigateToDeal from "../../hooks/useNavigateToDeal";
import useRowClassName from "../../hooks/useRowClassName";
import useValidateDeal from "../../hooks/useValidateDeal";
import DetailsType from "../../interfaces/DetailsType";
import { GeoCoordinate } from "../../interfaces/GeoCoordinate";
import { IDealState } from "../../interfaces/IDeal";
import Shape from "../../interfaces/Shape";
import { ClosableOrder } from "../../store/ClosableStore";
import AttachmentFile from "../AttachmentFile";
import commonStyles from "../Common.module.css";
import detailsStyles from "../Details.module.css";
import DetailsPanel from "../DetailsPanel";
import editStyles from "../Edit.module.css";
import { CreateControls } from "../EditControls";
import DealIcon from "../icons/DealIcon";
import FileInput from "../inputs/FileInput";
import InputContainer from "../inputs/InputContainer";
import TextInput from "../inputs/TextInput";
import DealFields from "./DealFields";
import DealSatelliteScreenshot from "./DealSatelliteScreenshot";
import DealScreenshotWarningModal from "./DealScreenshotWarningModal";

function DealCreate({ initialState, latitude, longitude, shapes }: IDealCreateProps) {
    const navigate = useNavigate();
    const navigateToDeal = useNavigateToDeal();
    const createDeal = useDealCreate();
    const validateDeal = useValidateDeal(true);
    const pipedriveStore = usePipedriveStore();
    const drawMapStore = useDrawMapStore();
    const mapMarkerStore = useMapMarkerStore();
    const rowClassName = useRowClassName();
    const [satelliteScreenshot, setSatelliteScreenshot] = useState<File | null>(null);
    const [showScreenshotWarning, setShowScreenshotWarning] = useState(false);

    const formik = useFormik<IDealState>({
        initialValues: initialState,
        validate: validateDeal,
        onSubmit: async (values) => {
            const createdDealId = await createDeal(values, satelliteScreenshot);
            navigateToDeal(createdDealId, true);
        },
    });

    useEffect(() => {
        if (initialState)
            formik.resetForm({
                values: {
                    ...initialState,
                    nonFund: pipedriveStore.nonFundOptions.find(o => o.label === "No")?.label ?? pipedriveStore.nonFundOptions[0].label,
                    dealProfile: pipedriveStore.profileOptions[0].label,
                }
            });
    }, [initialState]);

    useEffect(() => {
        drawMapStore.drawDeal(shapes);
        mapMarkerStore.draw(new GeoCoordinate(latitude, longitude));

        return () => {
            drawMapStore.clear();
            mapMarkerStore.clear();
        };
    }, [latitude, longitude, shapes]);

    const close = () => navigate("/");
    const cancel = () => navigate(-1);
    useClosable(ClosableOrder.Medium, cancel);

    return (
        <DetailsPanel type={DetailsType.DealCreate} onClose={close}>
            <div className={detailsStyles.header}>
                <div className={detailsStyles.headerGroup}>
                    <DealIcon color="#25479A" />
                    <div className={detailsStyles.title}>
                        <span className={commonStyles.primary}>Add New Deal</span>
                    </div>
                </div>
            </div>
            <div className={commonStyles.separator}></div>
            <div className={detailsStyles.body}>
                <DealFields formik={formik}>
                    <div className={rowClassName}>
                        <TextInput formik={formik} label="NOTES" name="notes" placeholder="Enter notes..." />
                    </div>
                    <div className={rowClassName}>
                        <div className={editStyles.rowGroup}>
                            <InputContainer label="ATTACHMENTS" name="attachments" formik={formik}>
                                <DealSatelliteScreenshot
                                    onScreenshotReady={setSatelliteScreenshot}
                                />
                                {formik.values.attachments?.map((attachment, index) => (
                                    <AttachmentFile
                                        key={index}
                                        name={attachment.name}
                                        size={attachment.size}
                                        onDeleteClick={() => formik.setFieldValue("attachments", formik.values.attachments!.filter(a => a !== attachment))}
                                    />
                                ))}
                                <FileInput name="attachments" setTouched={formik.handleBlur} onFilesSelected={files => formik.setFieldValue("attachments", [
                                    ...formik.values.attachments!,
                                    ...files,
                                ])} />
                            </InputContainer>
                        </div>
                        <div className={editStyles.doubleWidthRowGroup}></div>
                    </div>
                </DealFields>
            </div>
            <div className={commonStyles.separator}></div>
            <div className={commonStyles.controls}>
                <div className={commonStyles.controlGroup}>
                    <CreateControls
                        isStateChanged={formik.dirty || drawMapStore.isResultChanged || !mapMarkerStore.isMatch(new GeoCoordinate(latitude, longitude))}
                        isCreateLoading={pipedriveStore.isDealsLoading.creating! || pipedriveStore.isOrganizationsLoading.creating! || pipedriveStore.isPersonsLoading.creating!}
                        onCreate={() => satelliteScreenshot ? formik.handleSubmit() : setShowScreenshotWarning(true)}
                        onCancel={cancel}
                    />
                </div>
                <div className={commonStyles.controlGroup}></div>
            </div>
            {showScreenshotWarning && (
                <DealScreenshotWarningModal
                    onClose={() => setShowScreenshotWarning(false)}
                    onConfirm={() => { setShowScreenshotWarning(false); formik.handleSubmit(); }}
                />
            )}
        </DetailsPanel>
    );
}

export default observer(DealCreate);

interface IDealCreateProps {
    initialState: IDealState;
    latitude: number;
    longitude: number;
    shapes: Shape[];
}